import { UseFormRegister, UseFormSetValue, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormEvent, useState } from 'react';

import { CountryISOCodes } from 'common/countries';
import {
  isValidUKPostcode,
  normalizeUKPostcode,
  formatUKPostcodeInput,
} from 'utils/address';

import { Input } from './Input';

interface PostalCodeProps {
  name: string;
  label?: string;
  countryIsocode?: string;
  error?: any;
  register?: UseFormRegister<FieldValues>;
  setValue?: UseFormSetValue<FieldValues>;
  readOnly?: boolean;
  className?: string;
  validate?: boolean;
  shouldValidate?: boolean;
}

const PostalCode: React.FC<PostalCodeProps> = ({
  name,
  label,
  countryIsocode,
  error,
  register,
  setValue,
  readOnly,
  className,
  validate,
  shouldValidate,
}) => {
  const { t } = useTranslation();
  const [postcodeError, setPostcodeError] = useState<string>('');

  // A UK postcode is up to 8 chars with the space (e.g. "SW1A 0AA") but only 7
  // without it (e.g. "SW1A0AA"), so the max length depends on the entered value.
  const [hasSpace, setHasSpace] = useState<boolean>(false);

  const isUK = countryIsocode === CountryISOCodes.UnitedKingdom;

  const validatePostalCode = (val: string) => {
    if (val && isUK) {
      if (!isValidUKPostcode(val)) {
        setPostcodeError(t('common:validations.postcode'));
      } else {
        setPostcodeError('');
      }
    }
  };

  // While typing: keep letters, digits and spaces (blocks ".", "/", etc.) and
  // uppercase live. This handler replaces react-hook-form's registered onChange,
  // so it must always sync the formatted value back to the form. Once the form
  // has been submitted, re-validate on each keystroke to match RHF's default
  // reValidateMode (which our custom onChange would otherwise bypass).
  const handleChange = (
    e: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (!setValue) return;

    const formatted = formatUKPostcodeInput(e.currentTarget.value);
    setHasSpace(!formatted || formatted.includes(' '));

    setValue(name, formatted, {
      shouldDirty: true,
      shouldValidate,
    });
  };

  const handleBlur = (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isUK && setValue) {
      // Normalize: uppercase + space before the inward code, trimmed.
      const normalized = normalizeUKPostcode(e.currentTarget.value);
      setHasSpace(!normalized || normalized.includes(' '));
      setValue(name, normalized, {
        shouldDirty: true,
        shouldValidate,
      });

      if (validate) {
        validatePostalCode(normalized);
      }
      return;
    }

    if (validate) {
      validatePostalCode(e.currentTarget.value.trim());
    }
  };

  return (
    <div className={className}>
      <Input
        name={name}
        label={label}
        register={register}
        onBlur={handleBlur}
        readOnly={readOnly}
        maxLength={isUK ? (hasSpace ? 8 : 7) : undefined}
        error={
          error || (postcodeError ? { message: postcodeError } : undefined)
        }
        {...(isUK && setValue ? { onChange: handleChange } : {})}
      />
    </div>
  );
};

export default PostalCode;
